import { endOfDay, isBefore } from 'date-fns';
import { useLang } from 'hooks/lang';

import {
  actions,
  isOwner,
  getTaskStatusConfig,
  projectStatusByCode,
  taskStatusesConfig,
  isProjectFullBlocked,
} from 'entities';

import { DateSettingsIcon } from 'components/Icons';

import { useToast } from 'providers/Toast';
import { useSingleJustification } from 'providers/Justification';

import { getLocalDateString } from 'utils/date';

import { useUser } from 'hooks';

import { registerTaskOption } from '../options';

export default registerTaskOption({
  Icon: DateSettingsIcon,

  getLabel: (t) => t('request-date-change'),

  action: 'REQUEST_DATE_CHANGE',

  shortcutKey: 'A',

  useOption: ({ project, updateTask, task }) => {
    const { t } = useLang();
    const loggedUser = useUser();

    const { addToast } = useToast();

    const estimatedFinish = new Date(project?.expectedDate as string);

    const { dateChangeRequest } = task;

    const statusConfig = getTaskStatusConfig(task.status);
    const projectConfig = projectStatusByCode[project.status];

    const canChangeDate = [
      statusConfig.canChangeDate,
      projectConfig.isStartedStatus,
      isOwner(task, loggedUser),
      !dateChangeRequest,
      !isProjectFullBlocked(project.status),
    ].every(Boolean);

    const shouldDisableDate = (date: Date | null): boolean => {
      if (!date) return false;

      return isBefore(endOfDay(estimatedFinish), date);
    };

    const changeDate = actions.projects.requestTaskDateChange.use({
      onSuccess: (_, { date, justification }) => {
        updateTask((old) =>
          isOwner(project, loggedUser)
            ? { ...old, expectedDate: date }
            : {
                ...old,
                status: taskStatusesConfig.pendingApproval.code,
                dateChangeRequest: {
                  date,
                  priorStatus: old.status,
                  justification,
                },
              },
        );
      },
    });

    const handleChangeClick = useSingleJustification({
      text: t('why-is-this-change-necessary-pick-the-ideal-date-for-this-task'),

      type: 'date',

      cancel: () => addToast({ title: t('expected-date-change-aborted') }),

      shouldDisableDate,

      onConfirm: async (justification, date) => {
        await changeDate.execute({
          task: task.id,
          project: project.id,
          justification,
          date: getLocalDateString(date!),
        });
      },
    });

    return {
      action: handleChangeClick,

      loading: changeDate.loading,

      isEnabled: canChangeDate,
    };
  },
});
